import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Heart, MapPin, Eye, Share2, Flag, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ListingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);

  // Fetch listing from backend
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/listings/${id}`);
        setListing(response.data);
      } catch (error) {
        console.error('Error fetching listing:', error);
        if (error.response?.status === 404) {
          setListing(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchListing();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-red-500" />
          <p className="mt-4 text-gray-600">Loading listing...</p>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Listing not found</h1>
          <Button onClick={() => navigate('/')}>Back to Home</Button>
        </div>
      </div>
    );
  }

  // Remove related listings for now (would need separate API call)
  const relatedListings = [];

  const handleMessage = () => {
    toast({
      title: 'Opening Chat',
      description: `Starting conversation with ${listing.seller_name}`,
    });
    setTimeout(() => navigate('/messages'), 1000);
  };

  const handleShare = () => {
    // Copy URL to clipboard
    navigator.clipboard.writeText(window.location.href);
    toast({
      title: 'Link Copied',
      description: 'Listing link copied to clipboard',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 hover:bg-gray-100"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Main Image */}
            <div className="relative bg-white rounded-xl overflow-hidden shadow-sm">
              <div className="aspect-square">
                {listing.images && listing.images.length > 0 ? (
                  <img
                    src={listing.images[0]}
                    alt={listing.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <p className="text-gray-400">No image available</p>
                  </div>
                )}
              </div>
              <div className="absolute top-4 right-4 flex space-x-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setIsLiked(!isLiked);
                    toast({
                      title: isLiked ? 'Removed from favorites' : 'Added to favorites',
                    });
                  }}
                  className="bg-white/90 backdrop-blur-sm hover:bg-white rounded-full"
                >
                  <Heart
                    className={`w-5 h-5 ${
                      isLiked ? 'fill-red-500 text-red-500' : 'text-gray-700'
                    }`}
                  />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleShare}
                  className="bg-white/90 backdrop-blur-sm hover:bg-white rounded-full"
                >
                  <Share2 className="w-5 h-5 text-gray-700" />
                </Button>
              </div>
            </div>

            {/* Description Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Description</h2>
              <p className="text-gray-700 leading-relaxed">{listing.description}</p>

              <Separator className="my-6" />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Condition</p>
                  <p className="font-semibold text-gray-900">{listing.condition}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Category</p>
                  <p className="font-semibold text-gray-900 capitalize">{listing.category}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Location</p>
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4 text-gray-600" />
                    <p className="font-semibold text-gray-900">{listing.location}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Views</p>
                  <div className="flex items-center space-x-1">
                    <Eye className="w-4 h-4 text-gray-600" />
                    <p className="font-semibold text-gray-900">{listing.views}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Related Listings */}
            {relatedListings.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Similar Listings</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {relatedListings.map((related) => (
                    <div
                      key={related.id}
                      onClick={() => navigate(`/listing/${related.id}`)}
                      className="cursor-pointer group"
                    >
                      <div className="aspect-square rounded-lg overflow-hidden mb-2">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <p className="text-sm font-medium text-gray-900 line-clamp-2 mb-1">
                        {related.title}
                      </p>
                      <p className="text-sm font-bold text-red-500">${related.price}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Seller Info and Actions */}
          <div className="space-y-4">
            {/* Price Card */}
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-20">
              <div className="mb-6">
                <p className="text-sm text-gray-500 mb-1">Price</p>
                <p className="text-4xl font-bold text-red-500">${listing.price}</p>
              </div>

              <Button
                onClick={handleMessage}
                className="w-full bg-red-500 hover:bg-red-600 text-white py-6 text-lg mb-3"
              >
                Chat with Seller
              </Button>

              <Button variant="outline" className="w-full py-6 text-lg">
                Make an Offer
              </Button>

              <Separator className="my-6" />

              {/* Seller Info */}
              <div>
                <p className="text-sm text-gray-500 mb-3">Seller Information</p>
                <div
                  onClick={() => navigate(`/profile/${listing.seller_id}`)}
                  className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={listing.seller_avatar} />
                    <AvatarFallback>{listing.seller_name[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{listing.seller_name}</p>
                    <div className="flex items-center space-x-1 text-sm">
                      <span className="text-yellow-500">★</span>
                      <span className="text-gray-600">{listing.seller_rating} rating</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              <Separator className="my-6" />

              {/* Safety Tips */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Flag className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900 text-sm mb-1">Safety Tips</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Meet in a public place</li>
                      <li>• Check the item before payment</li>
                      <li>• Don't share financial info</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingDetailPage;
